<template>
  <v-dialog v-model="dialog" max-width="420" @keydown.esc="dialog = false">
    <v-card style="border-radius:16px; overflow:hidden;">

      <div class="modal-header">
        <v-icon color="#00BCD4" size="20" class="mr-2">mdi-cash-edit</v-icon>
        <span class="modal-title">Unesi cijenu</span>
        <v-spacer />
        <button class="modal-close" @click="dialog = false">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">
        <div class="mp-product-name">{{ product && product.name }}</div>

        <v-row dense class="mb-1">
          <v-col :cols="wholesaleView ? 6 : 12">
            <v-text-field
              ref="mpcInput"
              :value="mpc.toFixed(2)"
              label="Maloprodajna cijena"
              type="number" min="0" step="0.01"
              outlined dense hide-details
              class="wi-input"
              @focus="$event.target.select()"
              @blur="v => { mpc = parseFloat(v.target.value) || 0; onMpcChange() }"
              @keydown.enter="e => { e.target.blur(); $nextTick(() => { if (!wholesaleView) onConfirm() }) }"
            />
          </v-col>
          <v-col v-if="wholesaleView" cols="6">
            <v-text-field
              ref="vpcInput"
              :value="vpc.toFixed(2)"
              label="Veleprodajna cijena"
              type="number" min="0" step="0.01"
              outlined dense hide-details
              class="wi-input"
              @focus="$event.target.select()"
              @blur="v => { vpc = parseFloat(v.target.value) || 0; onVpcChange() }"
              @keydown.enter="e => { e.target.blur(); $nextTick(onConfirm) }"
            />
          </v-col>
        </v-row>

        <v-row dense class="mb-1">
          <v-col cols="12">
            <v-select
              v-model="pdv"
              :items="priceTaxes"
              item-text="name"
              item-value="rate"
              return-object
              label="PDV"
              outlined dense hide-details
              class="wi-input"
              :messages="!isTaxed ? 'Firma nije u sustavu PDV-a' : ''"
              @change="onPdvChange"
            />
          </v-col>
        </v-row>
      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="dialog = false">Odustani</button>
        <button class="btn-confirm" :disabled="mpc <= 0" @click="onConfirm">
          <v-icon size="16" color="white" class="mr-1">mdi-check</v-icon>
          Dodaj u košaricu
        </button>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { df } from '@/plugins/firebase'
import state from '@/state'
import { clone } from '@/plugins/utils'

const FALLBACK_TAXES = [
  { name: 'PDV 0%', rate: 0 },
  { name: 'PDV 5%', rate: 500 },
  { name: 'PDV 13%', rate: 1300 },
  { name: 'PDV 25%', rate: 2500 }
]

export default {
  name: 'ShopManualPriceModal',

  props: {
    wholesaleView: { type: Boolean, default: false }
  },

  data () {
    return {
      dialog: false,
      product: null,
      mpc: 0,
      vpc: 0,
      pdv: { name: 'PDV 25%', rate: 2500 },
      priceTaxes: []
    }
  },

  computed: {
    isTaxed () {
      return state.getCurrentCompany().is_taxed
    },

    taxRate () {
      return (this.pdv && this.pdv.rate ? this.pdv.rate : 0) / 10000
    }
  },

  methods: {
    async open (product, taxRate) {
      this.product = product
      this.mpc = 0
      this.vpc = 0
      await this.loadTaxes()
      if (taxRate != null) {
        const match = this.priceTaxes.find(t => Math.abs(t.rate / 10000 - taxRate) < 0.001)
        this.pdv = match || this.pdv
      }
      this.dialog = true
      this.focusAndSelect(this.wholesaleView ? 'vpcInput' : 'mpcInput')
    },

    async loadTaxes () {
      try {
        const doc = await df.doc('project_constants/taxes').get()
        const data = doc.data()
        const taxes = (data && data.pdv && data.pdv.length > 0) ? data.pdv : FALLBACK_TAXES
        if (!this.isTaxed) {
          this.priceTaxes = clone(taxes.map(tax => ({ ...tax, disabled: tax.rate !== 0 })))
          this.pdv = this.priceTaxes.find(tax => tax.rate === 0) || this.priceTaxes[0]
        } else {
          this.priceTaxes = clone(taxes)
          this.pdv = this.priceTaxes.find(tax => tax.rate === 2500) || this.priceTaxes[this.priceTaxes.length - 1]
        }
      } catch (e) {
        console.error('ShopManualPriceModal: greška pri dohvatu poreznih stopa', e)
        this.priceTaxes = FALLBACK_TAXES
        this.pdv = this.isTaxed ? FALLBACK_TAXES[3] : FALLBACK_TAXES[0]
      }
    },

    focusAndSelect (ref) {
      // v-dialog only mounts its content the first time it opens ("boots"),
      // which can land after Vue's own $nextTick — wait one more tick via
      // setTimeout so the input actually exists in the DOM before focusing.
      this.$nextTick(() => {
        setTimeout(() => {
          const input = this.$refs[ref]?.$el.querySelector('input')
          if (input) {
            input.focus()
            input.select()
          }
        }, 0)
      })
    },

    onMpcChange () {
      this.vpc = parseFloat((this.mpc / (1 + this.taxRate)).toFixed(2))
      if (this.wholesaleView) this.focusAndSelect('vpcInput')
    },

    onVpcChange () {
      this.mpc = parseFloat((this.vpc * (1 + this.taxRate)).toFixed(2))
    },

    onPdvChange () {
      // MPC je uvijek polazna vrijednost — preračunaj VPC na promjenu PDV-a
      this.vpc = parseFloat((this.mpc / (1 + this.taxRate)).toFixed(2))
    },

    onConfirm () {
      if (this.mpc <= 0) {
        return
      }
      this.$emit('add-item', {
        product: this.product,
        mpcCents: Math.round(this.mpc * 100),
        vpcCents: Math.round(this.vpc * 100),
        pdv: { name: this.pdv.name, rate: this.pdv.rate }
      })
      this.dialog = false
    }
  }
}
</script>

<style scoped>
.modal-header {
  display: flex; align-items: center;
  padding: 18px 20px 14px; border-bottom: 1px solid #F1F5F9;
}
.modal-title { font-size: 15px; font-weight: 700; color: #1e293b; }
.modal-close {
  background: transparent; border: none; cursor: pointer;
  color: #B0BEC5; display: flex; align-items: center;
  padding: 4px; border-radius: 6px; transition: all 0.15s;
}
.modal-close:hover { background: #F1F5F9; color: #374151; }

.modal-body { padding: 18px 20px 10px; }

.mp-product-name {
  font-size: 14px; font-weight: 600; color: #1e293b;
  margin-bottom: 12px;
}

.wi-input >>> .v-input__slot { border-radius: 8px !important; background: #F8FAFC !important; }
.wi-input >>> fieldset { border-color: #E2E8F0 !important; border-radius: 8px !important; }
.wi-input >>> .v-input__slot:hover fieldset { border-color: #00BCD4 !important; }
.wi-input >>> input[type=number]::-webkit-outer-spin-button,
.wi-input >>> input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.wi-input >>> input[type=number] { -moz-appearance: textfield; }

.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 14px 20px 18px; border-top: 1px solid #F1F5F9;
}
.btn-cancel {
  padding: 10px 20px; background: #F8FAFC;
  border: 1.5px solid #E2E8F0; border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.15s;
}
.btn-cancel:hover { background: #F1F5F9; }
.btn-confirm {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 22px;
  background-color: #00BCD4 !important; background: #00BCD4 !important;
  color: #fff !important; border-radius: 10px; border: none !important;
  font-size: 13px; font-weight: 700; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,188,212,0.3); transition: background-color 0.15s;
}
.btn-confirm:hover:not(:disabled) { background-color: #0097A7 !important; background: #0097A7 !important; }
.btn-confirm:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
</style>
